import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, ArrowRight, Loader2, CheckCircle } from 'lucide-react';

interface EmailStepProps {
  onNext: (email: string) => void;
  isLoading?: boolean;
  error?: string | null;
}

const EmailStep: React.FC<EmailStepProps> = ({ onNext, isLoading = false, error }) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const trimmedEmail = email.trim();
    
    if (!trimmedEmail) {
      setEmailError('Το email είναι υποχρεωτικό');
      setIsValidEmail(false);
      return false;
    }
    
    if (!emailRegex.test(trimmedEmail)) {
      setEmailError('Παρακαλώ εισάγετε ένα έγκυρο email');
      setIsValidEmail(false);
      return false;
    }
    
    setEmailError('');
    setIsValidEmail(true);
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateEmail(email)) {
      onNext(email.trim());
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    
    // Clear previous errors and validate in real-time
    if (value) {
      validateEmail(value);
    } else {
      setEmailError('');
      setIsValidEmail(false);
    }
  };

  // Show server error if present
  const displayError = error || emailError;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="mx-auto mt-8 max-w-md"
    >
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
          <Mail className="h-8 w-8 text-blue-600" />
        </div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900">Πρόσκληση Νέου Μέλους</h2>
        <p className="text-gray-600">Εισάγετε το email του χρήστη που θέλετε να προσκαλέσετε στην ομάδα σας</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-700">
            Email Address *
          </label>
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2">
              <Mail className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="user@example.com"
              className={`w-full rounded-xl border bg-white pl-12 pr-12 py-3 shadow-sm transition-all duration-200 focus:border-transparent focus:ring-2 ${
                displayError
                  ? 'border-red-300 focus:ring-red-500'
                  : isValidEmail
                  ? 'border-green-300 bg-green-50 focus:ring-green-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              disabled={isLoading}
            />
            {/* Email validation indicator */}
            {isValidEmail && email.trim() && (
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
            )}
          </div>
          
          {/* Error display */}
          {displayError && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 text-sm text-red-600"
            >
              {displayError}
            </motion.p>
          )}
        </div>

        <motion.button
          type="submit"
          disabled={isLoading || !isValidEmail}
          whileHover={{ scale: isLoading || !isValidEmail ? 1 : 1.02 }}
          whileTap={{ scale: isLoading || !isValidEmail ? 1 : 0.98 }}
          className={`flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3 font-medium shadow-lg transition-all duration-200 ${
            isLoading || !isValidEmail
              ? 'cursor-not-allowed bg-gray-300 text-gray-500'
              : 'bg-blue-600 text-white shadow-blue-200 hover:bg-blue-700'
          }`}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Επαλήθευση...
            </>
          ) : (
            <>
              Συνέχεια
              <ArrowRight className="h-5 w-5" />
            </>
          )}
        </motion.button>
      </form>

      {/* Email format help */}
      {email.trim() && !isValidEmail && !isLoading && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 rounded-xl bg-yellow-50 border border-yellow-200 p-4"
        >
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <Mail className="h-3 w-3 text-yellow-600" />
            </div>
            <div>
              <p className="text-yellow-800 text-sm font-medium mb-1">
                Μη έγκυρη μορφή email
              </p>
              <p className="text-yellow-700 text-xs">
                Παρακαλώ εισάγετε ένα έγκυρο email (π.χ. user@company.com)
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Instructions */}
      {!email.trim() && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 rounded-xl bg-blue-50 p-4"
        >
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <Mail className="h-3 w-3 text-blue-600" />
            </div>
            <div>
              <p className="text-blue-800 text-sm font-medium mb-1">
                Εισάγετε email για πρόσκληση
              </p>
              <p className="text-blue-700 text-xs">
                Ο χρήστης θα λάβει email πρόσκλησης στη διεύθυνση που θα εισάγετε
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default EmailStep;